import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Users, Flame, RefreshCw } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import LeadDrawer from "@/components/admin/LeadDrawer";
import LeadsTable from "@/components/admin/LeadsTable";
import { API, authed } from "@/lib/api";

export const StatCard = ({ icon: Icon, label, value, testid, tone }) => (
  <div className="bg-white rounded-2xl border p-5 sm:p-6 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tone}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <p className="text-xs sm:text-sm text-stone-500 font-medium">{label}</p>
      <p className="text-3xl font-display font-semibold" data-testid={testid}>{value ?? "–"}</p>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { schoolId } = useParams();
  const { getToken } = useAuth();
  const [leads, setLeads] = useState(null);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const opts = await authed(getToken);
      const [l, s] = await Promise.all([axios.get(`${API}/schools/${schoolId}/leads`, opts), axios.get(`${API}/schools/${schoolId}/stats`, opts)]);
      setLeads(l.data);
      setStats(s.data);
    } catch {
      setLeads((prev) => prev || []);
      toast.error("Could not load leads. Please refresh.");
    }
  }, [getToken, schoolId]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminShell>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Admission leads</h1>
          <p className="text-sm text-stone-500 mt-1">Enquiries from your chat assistant and enquiry form.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 rounded-full border bg-white text-sm hover:bg-stone-100 transition-colors" data-testid="refresh-leads-button">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <StatCard icon={Users} label="Total Leads" value={stats?.total_leads} testid="stat-total-leads" tone="bg-brand-soft text-brand" />
        <StatCard icon={Flame} label="Hot Leads This Week" value={stats?.hot_leads_this_week} testid="stat-hot-leads" tone="bg-red-100 text-red-600" />
      </div>

      <LeadsTable leads={leads} onSelect={setSelected} />
      <LeadDrawer lead={selected} onClose={() => setSelected(null)} />
    </AdminShell>
  );
}
